/**
 * Vibration utility for mobile devices
 * Provides haptic feedback patterns for touch interactions
 *
 * Note: Chrome on Android may accept vibration calls (return true) but still
 * not vibrate if system-level touch feedback/haptics are disabled.
 * This is a browser/OS limitation, not a code issue.
 * The code will work correctly on devices with haptic feedback enabled.
 */

/**
 * Get vibration strength from settings
 * Returns duration in milliseconds (0 = disabled)
 */
export const getVibrationStrength = (): number => {
  try {
    const saved = localStorage.getItem("vibrationSettings");
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.strength || 10; // Default to 10ms
    }
  } catch (e) {
    // Ignore errors, use default
  }
  return 10; // Default to 10ms
};

/**
 * Check if the device supports vibration
 */
export const isVibrationSupported = (): boolean => {
  return (
    typeof navigator !== "undefined" &&
    "vibrate" in navigator &&
    typeof navigator.vibrate === "function"
  );
};

/**
 * Check if the current context is secure (required for Vibration API)
 * Secure contexts include: HTTPS, localhost, 127.0.0.1
 */
export const isSecureContext = (): boolean => {
  return typeof window !== "undefined" && window.isSecureContext === true;
};

/**
 * Vibration patterns for different interaction types
 * Note: Many devices have a minimum vibration duration (~10-20ms),
 * so we use slightly longer durations to ensure reliability
 */
export const VibrationPatterns = {
  /**
   * Light vibration for single tap/touch
   * Duration: 5ms - very minimal feedback
   */
  SINGLE_TAP: 5,

  /**
   * Medium vibration for button press
   * Duration: 5ms - very minimal feedback
   */
  BUTTON_PRESS: 5,

  /**
   * Strong vibration for important actions
   * Duration: 10ms - minimal feedback
   */
  STRONG: 10,
} as const;

/**
 * Trigger vibration with a specific pattern
 * @param pattern - Vibration duration in milliseconds or pattern array
 * @returns true if vibration was triggered, false otherwise
 */
export const vibrate = (pattern: number | number[]): boolean => {
  // Check secure context first - Vibration API requires HTTPS or localhost
  if (!isSecureContext()) {
    return false;
  }

  if (!isVibrationSupported()) {
    return false;
  }

  try {
    // Ensure minimum duration for reliability
    let adjustedPattern: number | number[];
    if (typeof pattern === "number") {
      // Minimum 5ms - some devices accept shorter durations
      adjustedPattern = Math.max(pattern, 5);
    } else {
      adjustedPattern = pattern;
    }

    const result = navigator.vibrate(adjustedPattern);
    return result !== false;
  } catch (error) {
    return false;
  }
};

/**
 * Trigger vibration for a single tap/touch
 */
export const vibrateTap = (): boolean => {
  return vibrate(VibrationPatterns.SINGLE_TAP);
};

/**
 * Trigger vibration for a button press
 * Uses the strength setting from user preferences
 */
export const vibratePress = (): boolean => {
  const strength = getVibrationStrength();
  if (strength === 0) {
    return false; // Vibration disabled
  }
  return vibrate(strength);
};

/**
 * Trigger strong vibration for important actions
 */
export const vibrateStrong = (): boolean => {
  return vibrate(VibrationPatterns.STRONG);
};
